import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Send, Mic, Loader2 } from "lucide-react";
import VoiceRecorder from "./VoiceRecorder";

interface ChatInputProps {
  onSendMessage: (message: string) => Promise<boolean>;
  onSendVoiceMessage?: (audioBlob: Blob, transcription?: string) => Promise<boolean>;
  onTyping: () => void;
  sending: boolean;
  connectionStatus: 'connected' | 'disconnected' | 'reconnecting';
}

const ChatInput = ({ 
  onSendMessage, 
  onSendVoiceMessage,
  onTyping, 
  sending, 
  connectionStatus 
}: ChatInputProps) => {
  const [newMessage, setNewMessage] = useState("");
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSend = async () => {
    if (!newMessage.trim() || sending) return;
    
    const success = await onSendMessage(newMessage.trim());
    if (success) {
      setNewMessage("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else {
      onTyping();
    }
  };

  const handleVoiceMessage = async (audioBlob: Blob, transcription?: string) => {
    if (onSendVoiceMessage) {
      const success = await onSendVoiceMessage(audioBlob, transcription);
      if (success) {
        setShowVoiceRecorder(false);
      }
      return success;
    }
    return false;
  };

  // Auto-resize textarea with 5-line maximum
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const scrollHeight = textarea.scrollHeight;
      const lineHeight = 24; // 1.5rem in pixels (text-sm)
      const maxHeight = lineHeight * 5 + 16; // 5 lines + padding
      
      if (scrollHeight <= maxHeight) {
        textarea.style.height = `${scrollHeight}px`;
        textarea.style.overflowY = 'hidden';
      } else {
        textarea.style.height = `${maxHeight}px`;
        textarea.style.overflowY = 'auto';
      }
    }
  }, [newMessage]);

  const isDisabled = sending || connectionStatus === 'disconnected';

  if (showVoiceRecorder) {
    return (
      <div className="fixed bottom-[60px] left-0 right-0 bg-carbon border-t border-ice-white/20 z-50">
        <div className="max-w-md mx-auto p-4">
          <VoiceRecorder 
            onSendVoiceMessage={handleVoiceMessage}
            disabled={isDisabled}
          />
          <Button
            onClick={() => setShowVoiceRecorder(false)}
            variant="ghost"
            className="w-full mt-3 text-ice-white/60 hover:text-ice-white"
          >
            Back to text
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-[60px] left-0 right-0 bg-carbon border-t border-ice-white/20 z-50">
      <div className="max-w-md mx-auto p-4 my-0 py-[9px] px-[24px]">
        <div className="flex space-x-2">
          <Textarea 
            ref={textareaRef}
            value={newMessage} 
            onChange={e => {
              setNewMessage(e.target.value);
              onTyping();
            }} 
            onKeyDown={handleKeyPress} 
            placeholder={isDisabled ? "Connecting..." : "Type a message..."} 
            className="flex-1 bg-ice-white/10 border-ice-white/20 text-ice-white placeholder:text-ice-white/50 focus:border-ice-white/40 resize-none min-h-[40px] text-sm leading-6" 
            disabled={isDisabled} 
          />
          
          {/* Voice Message Button */}
          {onSendVoiceMessage && (
            <Button
              onClick={() => setShowVoiceRecorder(true)}
              disabled={isDisabled}
              variant="outline"
              size="sm"
              className="px-3 border-electric-violet text-electric-violet hover:bg-electric-violet/10"
            >
              <Mic className="w-4 h-4" />
            </Button>
          )}
          
          {/* Send Button */}
          <Button 
            onClick={handleSend} 
            disabled={!newMessage.trim() || isDisabled} 
            size="sm" 
            className="px-3 bg-pink-500 hover:bg-pink-600 text-white"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;